<template>
  <div id="OrgSettled" class="org-settled-page">
    <!-- 顶部导航栏 -->
    <nav-bar class="about-nav-bar" />

    <!-- 顶部横幅 - 教育直播 AI 盈利赋能平台 -->
    <section class="hero-section" :style="heroSectionStyle">
      <div class="hero-content">
        <div class="hero-text">
          <h1 class="hero-title">{{ heroTitleDisplay }}</h1>
          <p class="hero-desc">
            用智能驱动增效、靠技术促增长，全链路助力机构快速盈利赋能平台
          </p>
        </div>
        <div class="button-wrap">
          <button class="free-trial-btn" @click="handleFreeTrial">
            <span class="text">免费试用</span>
            <span class="button__icon-wrapper">
              <svg
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="button__icon-svg"
                width="10"
              >
                <path
                  d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
                  fill="currentColor"
                ></path>
              </svg>

              <svg
                viewBox="0 0 14 15"
                fill="none"
                width="10"
                xmlns="http://www.w3.org/2000/svg"
                class="button__icon-svg button__icon-svg--copy"
              >
                <path
                  d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
                  fill="currentColor"
                ></path>
              </svg>
            </span>
          </button>
          <button class="consult-btn" @click="handleSettle">立即咨询</button>
        </div>
        <img
          src="https://cdn.moguocdn.com/cdn/org/settled/ai-platform.png"
          :alt="aiPlatform.name"
          class="hero-image"
          ref="heroImage"
          :class="{ 'hero-image--highlight': heroImageHighlighted }"
        />
      </div>
    </section>

    <!-- 直播间模块 -->
    <section v-if="liveRoom" class="section live-room-section">
      <div class="container">
        <h2 class="section-title left">{{ liveRoom.name }}</h2>
        <div class="live-room-layout">
          <div class="live-room-info">
            <div class="live-room-tabs" role="tablist" aria-label="直播间类型">
              <button
                v-for="(item, index) in liveRoom.list"
                :key="item.name + index"
                class="live-room-tab"
                :class="{
                  'live-room-tab--active': activeLiveRoomIndex === index,
                }"
                role="tab"
                :aria-selected="activeLiveRoomIndex === index"
                :tabindex="activeLiveRoomIndex === index ? 0 : -1"
                @click="handleLiveRoomChange(index)"
                @keydown="handleLiveRoomKeydown($event, index)"
              >
                <span class="live-room-tab__title">{{ item.name }}</span>
                <span
                  v-if="activeLiveRoomIndex === index && item.desc"
                  class="live-room-tab__desc"
                >
                  {{ item.desc }}
                </span>
              </button>
            </div>
          </div>
          <div class="live-room-visual" v-if="activeLiveRoomItem">
            <div class="live-room-image-wrap">
              <img
                :src="activeLiveRoomItem.img"
                :alt="activeLiveRoomItem.name"
                class="live-room-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- AI - 驱动效率模块 -->
    <section v-if="aiModule" class="ai-efficiency-section">
      <div
        class="ai-efficiency-bg"
        :style="{
          '--ai-bg-image': aiModule.bg_img || 'none',
        }"
      >
        <div class="ai-efficiency-container">
          <div class="ai-efficiency-tag">{{ aiModule.name }}</div>
          <h2 class="ai-efficiency-title">{{ aiModule.desc }}</h2>
          <div class="ai-efficiency-cards">
            <div
              v-for="(item, index) in aiModule.list"
              :key="item.name + index"
              class="ai-efficiency-card"
            >
              <div class="ai-efficiency-card__icon">
                <img :src="item.img" :alt="item.name" />
                <h3 class="ai-efficiency-card__title">{{ item.name }}</h3>
              </div>
              <div class="ai-efficiency-card__content">
                <p class="ai-efficiency-card__desc">{{ item.desc }}</p>
                <p class="ai-efficiency-card__text">{{ item.content }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="ssr-variant hidden-1c0dkx6"
        :style="{ '--rotate-angle': rotateAngle + 'deg' }"
      >
        <div class="framer-1stzqw2-container">
          <div class="framer-gzvjR framer-143nf7r framer-v-143nf7r">
            <div class="framer-1ehqw8y"></div>
            <div class="framer-1bce4jm"></div>
            <div class="framer-xv7igh"></div>
            <div class="framer-xknqa4"></div>
            <div class="framer-1p1pzny"></div>
            <div class="framer-166olkj">
              <div class="framer-1socfl3"></div>
              <div class="framer-y8aghn"></div>
              <div class="framer-1w9dmf0"></div>
              <div class="framer-88dz9y"></div>
              <div class="framer-trql9i"></div>
              <div class="framer-138py8g"></div>
              <div class="framer-1yx3zku"></div>
            </div>
            <div class="framer-19bj41o"></div>
          </div>
        </div>
      </div>
      <div class="ai-efficiency-bg-overlay"></div>
    </section>

    <!-- 作业布置、学习打卡与试题课后一体化支撑 -->
    <section v-if="homework" class="homework-section">
      <div class="homework-container">
        <h2 class="homework-title">{{ homework.name }}</h2>
        <div class="homework-tabs" role="tablist" aria-label="课后支持模块">
          <button
            v-for="(item, index) in homework.list"
            :key="item.name + index"
            class="homework-tab"
            :class="{ 'homework-tab--active': activeHomeworkIndex === index }"
            role="tab"
            :aria-selected="activeHomeworkIndex === index"
            :tabindex="activeHomeworkIndex === index ? 0 : -1"
            @click="handleHomeworkTabChange(index)"
            @keydown="handleHomeworkKeydown($event, index)"
          >
            {{ item.name }}
          </button>
        </div>
        <transition name="homework-slide" mode="out-in">
          <div
            v-if="activeHomeworkItem"
            :key="activeHomeworkIndex"
            class="homework-panel"
          >
            <div class="homework-info">
              <h3 class="homework-panel-title">
                {{ activeHomeworkItem.name }}
              </h3>
              <p v-if="activeHomeworkItem.desc" class="homework-panel-subtitle">
                {{ activeHomeworkItem.desc }}
              </p>
              <p class="homework-panel-text">
                {{ activeHomeworkItem.content }}
              </p>
              <ul
                v-if="activeHomeworkItem.tags && activeHomeworkItem.tags.length"
                class="homework-feature-list"
              >
                <li
                  v-for="(tag, tagIndex) in activeHomeworkItem.tags"
                  :key="tag + tagIndex"
                  class="homework-feature-item"
                >
                  <span class="dot"></span>
                  <span class="feature-text">{{ tag }}</span>
                </li>
              </ul>
            </div>
            <div class="homework-visual">
              <div class="homework-visual-card">
                <img
                  :src="activeHomeworkItem.img"
                  :alt="activeHomeworkItem.name"
                  class="homework-visual-image"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </section>

    <!-- 真实用户故事 -->
    <section v-if="realStories" class="stories-section">
      <div class="stories-container">
        <h2 class="stories-title">{{ realStories.name }}</h2>
        <div class="stories-marquee">
          <div class="stories-row stories-row--top">
            <div class="stories-track">
              <article
                v-for="(item, index) in storiesTopRowLoop"
                :key="`top-${index}-${item.name}`"
                class="story-card"
              >
                <p class="story-desc" v-html="item.desc"></p>
                <div class="story-user">
                  <div class="story-avatar-wrap">
                    <img
                      :src="item.avatar"
                      :alt="item.name"
                      class="story-avatar"
                    />
                  </div>
                  <div class="story-user-info">
                    <p class="story-name">{{ item.name }}</p>
                    <p class="story-position">{{ item.position }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
          <div class="stories-row stories-row--bottom">
            <div class="stories-track">
              <article
                v-for="(item, index) in storiesBottomRowLoop"
                :key="`bottom-${index}-${item.name}`"
                class="story-card"
              >
                <p class="story-desc" v-html="item.desc"></p>
                <div class="story-user">
                  <div class="story-avatar-wrap">
                    <img
                      :src="item.avatar"
                      :alt="item.name"
                      class="story-avatar"
                    />
                  </div>
                  <div class="story-user-info">
                    <p class="story-name">{{ item.name }}</p>
                    <p class="story-position">{{ item.position }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 常见问题 -->
    <section v-if="qa" class="section qa-section">
      <div class="container">
        <h2 class="section-title">常见问题(FAQs)</h2>
        <div class="qa-list">
          <div
            class="qa-item"
            v-for="(item, index) in qa.list"
            :key="index"
            :class="{ action: activeQaIndexes.includes(index) }"
          >
            <div class="qa-item-wrap">
              <div class="qa-question" @click="handleQaItemClick(index)">
                <div class="qa-content">
                  <p class="qa-text">{{ item.Q }}</p>
                </div>
                <div class="qa-question-icon-wrap">
                  <div class="qa-question-icon">
                    <div class="qa-question-icon-cross"></div>
                    <div class="qa-question-icon-line"></div>
                  </div>
                </div>
              </div>
              <div class="qa-answer">
                <div class="qa-content">
                  <p
                    class="qa-text qa-answer-text"
                    @click="handleFreeTrial"
                    v-html="item.A"
                  ></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="qa-contact">
          <div class="qa-contact__info">
            <p class="qa-contact__title">找不到您的答案吗?</p>
            <p class="qa-contact__desc">
              请与我们联系，我们很乐意为您提供帮助！
            </p>
          </div>
          <button class="qa-contact__button" @click="handleSettle">
            立即咨询
          </button>
        </div>
      </div>
    </section>

    <!-- 微信二维码区域 -->
    <section v-if="wechatEntry" class="wechat-entry-section">
      <div
        class="wechat-entry-bg"
        :style="{
          '--bg-img-left': wechatEntry.bg_img_left,
          '--bg-img-right': wechatEntry.bg_img_right,
        }"
      >
        <div class="container">
          <h2 class="wechat-entry-title">{{ wechatEntry.name }}</h2>
          <div class="wechat-entry-content">
            <div class="qr-code-wrap">
              <img
                :src="wechatEntry.qr_code"
                alt="微信二维码"
                class="qr-code-image"
              />
            </div>
            <div class="wechat-entry-text">
              <p
                v-for="(line, index) in wechatEntryDescLines"
                :key="index"
                class="desc-line"
              >
                {{ line }}
              </p>
              <el-button
                type="primary"
                class="free-trial-btn"
                @click="handleFreeTrial"
              >
                免费试用
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 咨询入口 -->
    <Consult :visible.sync="consultVisible" ref="Consult" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getUserInfo } from "@saas/server-api/src";
import Consult from "./components/Consult.vue";
import { NavBar } from "@saas/components/src";
import { getOrgSettledJson } from "@saas/server-api/src/json";
import { fastCreateOrganization } from "@saas/server-api/src/organization";

export default {
  name: "OrgSettled",
  components: {
    Consult,
    NavBar,
  },
  data: function () {
    return {
      jsonData: [],
      activeQaIndexes: [],
      activeLiveRoomIndex: 0,
      activeHomeworkIndex: 0,
      heroTitleDisplay: "教育直播 Al盈利赋能平台",
      heroTitleAnimationTimer: null,
      heroImageHighlighted: false,
      heroImageScrollHandler: null,
      consultVisible: false,
      rotateAngle: 0,
      rotateAnimationTimer: null,
      homeworkAutoSwitchTimer: null,
      aiPlatform: {
        name: "教育直播 Al盈利赋能平台",
        desc: "用智能驱动增效、靠技术促增长，全链路助力机构快速盈利赋能平台 ",
        bg_img: "https://cdn.moguocdn.com/cdn/org/settled/ai-platform-bg.png",
        img: "https://cdn.moguocdn.com/cdn/org/settled/ai-platform.png",
      },
      liveRoom: {
        key: "LiveRoom",
        name: "直播间",
        list: [
          {
            name: "大班课",
            desc: "支持万人同时在线，延迟小于 1 秒，适用于公开课、引流课、大型讲座，可快速扩大品牌影响力",
            img: "https://cdn.moguocdn.com/cdn/org/settled/live-room-big-class.png",
          },
          {
            name: "小班课",
            desc: "支持 1V1 至 1V12 多班型，低延迟直播；老师能管理课堂权限、添加课件，课中可使用定时、计时、答题器及共享白板，满足老师多样化教学需求。",
            img: "https://cdn.moguocdn.com/cdn/org/settled/live-room-small-class.png",
          },
          {
            name: "版权保护",
            desc: "通过显示机构水印等跑马灯结合防录屏、网页防火墙及版权声明等手段，多维度筑牢防护，显著降低内容版权安全风险。",
            img: "https://cdn.moguocdn.com/cdn/org/settled/live-room-copyright.png",
          },
        ],
      },
      aiModule: {
        key: "AIModule",
        name: "AI - 驱动效率",
        desc: "AI 智能梳课重点、解课时疑问，助力高效学习",
        list: [
          {
            name: "AI 看课",
            desc: "提高学习效果，快速吸收知识点",
            content:
              "回放课时支持 AI 总结及提问。学生不用反复回看找重点，复习效率翻倍，减轻老师答疑压力。",
            img: "https://cdn.moguocdn.com/cdn/org/settled/live-module-ai-look.png",
          },
          {
            name: "AI 笔记",
            desc: "帮助快速整理记录学习笔记和知识点",
            content:
              "AI 自动同步课程内容生成精准笔记，回放时可随时将 AI 笔记插入个人笔记，补充关键知识点辅助理解课程内容，学习更轻松。",
            img: "https://cdn.moguocdn.com/cdn/org/settled/live-module-ai-note.png",
          },
          {
            name: "AI 录题",
            desc: "AI 批量导入题目，提高识别准确度",
            content:
              "批量导入题目，精准识别内容，提升录题效率，快速构建专属题库为教师减负增效。",
            img: "https://cdn.moguocdn.com/cdn/org/settled/live-module-ai-question.png",
          },
        ],
      },
      homework: {
        key: "Homework",
        name: "作业布置、学习打卡与试题课后一体化支撑",
        bg_img: "https://cdn.moguocdn.com/cdn/org/settled/homework-bg.png",
        list: [
          {
            name: "作业系统",
            content:
              "老师可以像线下教学一样,给学生布置课后作业,作业包含多多个类型,可自动批阅及查看数据,功能齐全又强大!",
            img: "https://cdn.moguocdn.com/cdn/org/settled/homework-system.png",
            tags: ["丰富作业类型系统", "自动识别题型", "高效智能批改"],
          },
          {
            name: "打卡系统",
            content:
              "系统支持每日打卡和闯关挑战，激励培养学习习惯，让坚持变得轻松有趣！",
            img: "https://cdn.moguocdn.com/cdn/org/settled/homework-check.png",
            tags: ["每日打卡", "闯关打卡", "趣味学习"],
          },
          {
            name: "题库与试卷",
            content:
              "支持一键导入题目生成作业，课程作业、随堂测验等场景可直接复用作业模板，无需重复创建，让备课、测评更省心。",
            img: "https://cdn.moguocdn.com/cdn/org/settled/homework-ai-question.png",
            tags: [
              "导入、编辑、分类，题目可存库",
              "自动识别题型",
              "自动/手动组卷，省出卷时间",
            ],
          },
        ],
      },
      qa: {
        key: "QA",
        name: "常见问题(FAQs)",
        list: [
          {
            Q: "个人入驻流程是怎么样的?",
            A: "点击本页面<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>即可机构入驻，如遇任何问题可加下方微信或点击右侧电话咨询",
          },
          {
            Q: "机构入驻流程是怎样的？",
            A: "点击本页面<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>即可机构入驻，如遇任何问题可加下方微信或点击右侧电话咨询",
          },
          {
            Q: "直播间最多可多少人在线，延时时间是多少?",
            A: "为保证最佳教学效果，我们的直播间支持万人同时稳定在线，并采用超低延时技术，音画同步如面对面交流。实际承载力和具体延时因配置而异，您可以在<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>中亲自测试体验。",
          },
          {
            Q: "入驻平台后，课酬是否会出现拖欠?",
            A: "入驻后完成课程，即可在机构管理点击提现，点击<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>，立即入驻。",
          },
          {
            Q: "直播和回放课程是否有防盗和防录制?",
            A: "我们为您的知识产权提供了多重防护，包括跑马灯水印、防翻录机制等技术，有效防止非法扩散。点击<a href='https://web.zhixinoj.com/biz/' target='_blank'>免费试用</a>，查看课程防护效果",
          },
        ],
      },
      wechatEntry: {
        key: "WechatEntry",
        name: "扫 描 下 方 二 维 码 微 信 ， 1 对 1 专 属 顾 问 帮 您 搞 定 机 构 入 驻 ",
        desc: "扫描左侧微信添加专属顾问\n或点击下方“免费试用”直接体验",
        qr_code:
          "https://cdn.moguocdn.com/cdn/static/images/kehu_zixun_qrcode.png",
        bg_img_left: "https://cdn.moguocdn.com/cdn/org/settled/bg-img-left.png",
        bg_img_right:
          "https://cdn.moguocdn.com/cdn/org/settled/bg-img-right.png",
      },
    };
  },
  computed: {
    ...mapGetters(["isAuthenticated", "user"]),
    heroSectionStyle() {
      if (!this.aiPlatform || !this.aiPlatform.bg_img) {
        return {
          "--hero-bg-image": "none",
        };
      }
      return {
        "--hero-bg-image": `url(${this.aiPlatform.bg_img})`,
      };
    },
    activeLiveRoomItem() {
      if (!this.liveRoom || !Array.isArray(this.liveRoom.list)) {
        return null;
      }
      return this.liveRoom.list[this.activeLiveRoomIndex] || null;
    },
    activeHomeworkItem() {
      if (!this.homework || !Array.isArray(this.homework.list)) {
        return null;
      }
      return this.homework.list[this.activeHomeworkIndex] || null;
    },
    realStories() {
      return this.jsonData.find((item) => item.key === "RealStories");
    },
    storiesTopRow() {
      if (!this.realStories || !Array.isArray(this.realStories.list)) {
        return [];
      }
      const half = Math.ceil(this.realStories.list.length / 2);
      return this.realStories.list.slice(0, half);
    },
    storiesBottomRow() {
      if (!this.realStories || !Array.isArray(this.realStories.list)) {
        return [];
      }
      const half = Math.ceil(this.realStories.list.length / 2);
      const bottom = this.realStories.list.slice(half);
      if (!bottom.length) {
        return this.realStories.list.slice();
      }
      return bottom;
    },
    storiesTopRowLoop() {
      const top = this.storiesTopRow;
      return top.concat(top);
    },
    storiesBottomRowLoop() {
      const bottom = this.storiesBottomRow;
      return bottom.concat(bottom);
    },
    wechatEntryDescLines() {
      if (!this.wechatEntry || !this.wechatEntry.desc) {
        return [];
      }
      return this.wechatEntry.desc.split("\n").filter((line) => line.trim());
    },
  },
  watch: {
    aiPlatform: {
      handler(newVal) {
        if (newVal && newVal.name) {
          this.startHeroTitleAnimation(newVal.name);
          this.$nextTick(() => {
            this.initHeroImageAnimation();
          });
        }
      },
      immediate: true,
    },
    aiModule: {
      handler(newVal) {
        if (newVal) {
          this.startRotateAnimation();
        }
      },
      immediate: true,
    },
    homework: {
      handler(newVal) {
        if (newVal) {
          this.startHomeworkAutoSwitch();
        }
      },
      immediate: true,
    },
  },
  async created() {
    await this.apiGetOrgSettledJson();
  },
  async mounted() {
    this.initHeroImageAnimation();
    this.startRotateAnimation();
    this.startHomeworkAutoSwitch();
  },
  methods: {
    async apiGetOrgSettledJson() {
      const res = await getOrgSettledJson();
      this.jsonData = res || [];
    },
    async handleFreeTrial(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!this.isAuthenticated) {
        this.$loginDialog({
          success: async () => {
            const userInfo = await getUserInfo();
            this.jumpOrgManagement(userInfo.data?.org_id);
            window.location.reload();
            return;
          },
        });
        return;
      }
      this.jumpOrgManagement();
    },
    async jumpOrgManagement(orgId = 0) {
      if (this.user?.org_id || orgId) {
        window.open(process.env.VUE_APP_ADMIN_URL, "_blank");
        return;
      }
      try {
        this.$loading();
        const orgInfo = await fastCreateOrganization();
        if (orgInfo.code === 1) {
          window.open(process.env.VUE_APP_ADMIN_URL, "_blank");
          window.location.reload();
        } else {
          this.$message.error(orgInfo.message);
        }
      } catch (error) {
        console.error("fastCreateOrganization failed", error);
        this.$message.error("机构创建失败，请稍后重试");
      } finally {
        this.$loading().close();
      }
    },
    handleSettle() {
      // window.location.href = `${process.env.VUE_APP_ADMIN_URL}`;
      this.consultVisible = true;
    },
    handleLiveRoomChange(index) {
      if (index === this.activeLiveRoomIndex) {
        return;
      }
      if (!this.liveRoom || !this.liveRoom.list || !this.liveRoom.list[index]) {
        return;
      }
      this.activeLiveRoomIndex = index;
    },
    handleLiveRoomKeydown(event, index) {
      if (
        event.key !== "Enter" &&
        event.key !== " " &&
        event.key !== "Spacebar"
      ) {
        return;
      }
      event.preventDefault();
      this.handleLiveRoomChange(index);
    },
    handleHomeworkTabChange(index) {
      if (
        index === this.activeHomeworkIndex ||
        !this.homework ||
        !this.homework.list ||
        !this.homework.list[index]
      ) {
        return;
      }
      this.activeHomeworkIndex = index;
      this.resetHomeworkAutoSwitch();
    },
    startHomeworkAutoSwitch() {
      this.stopHomeworkAutoSwitch();
      if (
        !this.homework ||
        !this.homework.list ||
        this.homework.list.length <= 1
      ) {
        return;
      }
      this.homeworkAutoSwitchTimer = setInterval(() => {
        const nextIndex =
          (this.activeHomeworkIndex + 1) % this.homework.list.length;
        this.activeHomeworkIndex = nextIndex;
      }, 5000);
    },
    stopHomeworkAutoSwitch() {
      if (this.homeworkAutoSwitchTimer) {
        clearInterval(this.homeworkAutoSwitchTimer);
        this.homeworkAutoSwitchTimer = null;
      }
    },
    resetHomeworkAutoSwitch() {
      this.stopHomeworkAutoSwitch();
      this.startHomeworkAutoSwitch();
    },
    handleHomeworkKeydown(event, index) {
      if (
        event.key !== "Enter" &&
        event.key !== " " &&
        event.key !== "Spacebar"
      ) {
        return;
      }
      event.preventDefault();
      this.handleHomeworkTabChange(index);
    },
    clearHeroTitleAnimation() {
      if (this.heroTitleAnimationTimer) {
        clearInterval(this.heroTitleAnimationTimer);
        this.heroTitleAnimationTimer = null;
      }
    },
    startHeroTitleAnimation(text) {
      this.clearHeroTitleAnimation();
      if (!text) {
        this.heroTitleDisplay = "";
        return;
      }
      this.heroTitleDisplay = "";
      let index = 0;
      this.heroTitleAnimationTimer = setInterval(() => {
        this.heroTitleDisplay = text.slice(0, index + 1);
        index += 1;
        if (index >= text.length) {
          this.clearHeroTitleAnimation();
        }
      }, 80);
    },
    initHeroImageAnimation() {
      if (this.heroImageScrollHandler) {
        window.removeEventListener("scroll", this.heroImageScrollHandler);
        this.heroImageScrollHandler = null;
      }
      this.$nextTick(() => {
        const imageEl = this.$refs.heroImage;
        if (!imageEl) {
          this.heroImageHighlighted = false;
          return;
        }
        const updateHighlight = () => {
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop || 0;
          // console.log(scrollTop, imageEl.offsetTop);
          this.heroImageHighlighted = scrollTop >= imageEl.offsetTop - 200;
        };
        this.heroImageScrollHandler = updateHighlight;
        window.addEventListener("scroll", updateHighlight, { passive: true });
      });
    },
    handleQaItemClick(index) {
      const itemIndex = this.activeQaIndexes.indexOf(index);
      if (itemIndex === -1) {
        this.activeQaIndexes = [...this.activeQaIndexes, index];
        return;
      }
      this.activeQaIndexes = this.activeQaIndexes.filter(
        (activeIndex) => activeIndex !== index
      );
    },
    startRotateAnimation() {
      this.stopRotateAnimation();
      const baseAngle = this.aiModule?.rotate_angle
        ? parseFloat(this.aiModule.rotate_angle)
        : 0;
      this.rotateAngle = baseAngle;
      this.rotateAnimationTimer = setInterval(() => {
        this.rotateAngle += 1.5;
        if (this.rotateAngle >= 360) {
          this.rotateAngle = 0;
        }
      }, 16);
    },
    stopRotateAnimation() {
      if (this.rotateAnimationTimer) {
        clearInterval(this.rotateAnimationTimer);
        this.rotateAnimationTimer = null;
      }
    },
  },
  beforeDestroy() {
    this.clearHeroTitleAnimation();
    this.stopRotateAnimation();
    this.stopHomeworkAutoSwitch();
    if (this.heroImageScrollHandler) {
      window.removeEventListener("scroll", this.heroImageScrollHandler);
      this.heroImageScrollHandler = null;
    }
  },
};
</script>

<style lang="scss" scoped>
#OrgSettled {
  height: 100%;
  background: #fff;
  min-width: 1200px;
  width: 100%;
}

.org-settled-page {
  width: 100%;
  min-width: 1200px;
  background: #fff;
}

.container {
  width: 1200px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section {
  padding: 40px 0;
}

.section-title {
  font-weight: 600;
  font-size: 36px;
  color: #0d1400;
  text-align: center;
  margin-bottom: 60px;
  line-height: 1.2;
}

.section-title.left {
  text-align: left;
  width: 100%;
  display: block;
  margin-bottom: 32px;
  margin-top: 32px;
}

.section-desc {
  font-size: 16px;
  color: #515350;
  text-align: center;
  margin-bottom: 48px;
  line-height: 1.6;
}

// 顶部横幅
.hero-section {
  width: 100%;
  padding: 80px 0;
  // background: linear-gradient(135deg, #f4fff8 0%, #fefeff 87%, #f9fbff 100%);
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: var(--hero-bg-image);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  pointer-events: none;
}

.hero-content {
  width: 1200px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 0 20px;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.hero-image {
  flex-shrink: 0;
  max-width: 1136px;
  height: auto;
  border-radius: 24px;
  object-fit: cover;
  transform-origin: center center;
  transform: perspective(1600px) rotateX(30deg);
  transition: transform 0.8s ease;
}

.hero-image--highlight {
  transform: perspective(1600px) rotateX(0deg);
}

.hero-text {
  flex: 1;
}

.hero-title {
  font-weight: 600;
  font-size: 42px;
  color: #0d1400;
  margin-bottom: 12px;
  line-height: 1.3;
  text-align: center;
}

.hero-desc {
  font-size: 18px;
  color: #515350;
  line-height: 1.8;
  letter-spacing: 2px;
}

.button-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  .free-trial-btn {
    width: 160px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    cursor: pointer;
    background: linear-gradient(180deg, #00eb81 0%, #00c89d 100%);
    border-radius: 999px;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.137);
    .text {
      height: 100%;
      display: flex;
      font-size: 20px;
      align-items: center;
      justify-content: center;
      color: white;
      margin-right: -16px;
    }

    .button__icon-wrapper {
      flex-shrink: 0;
      width: 26px;
      height: 26px;
      position: relative;
      color: #00eb81;
      background-color: #fff;
      border-radius: 50%;
      display: grid;
      place-items: center;
      overflow: hidden;
    }

    .button__icon-svg--copy {
      position: absolute;
      transform: translate(-150%, 150%);
    }

    &:hover {
      .button__icon-wrapper {
        color: #00c89d;
      }
      .button__icon-svg:first-child {
        transition: transform 0.3s ease-in-out;
        transform: translate(150%, -150%);
      }
      .button__icon-svg--copy {
        transition: transform 0.3s ease-in-out 0.1s;
        transform: translate(0);
      }
    }

    &:active {
      transform: scale(0.97);
    }
  }
  .consult-btn {
    width: 160px;
    height: 56px;
    font-weight: 600;
    font-size: 20px;
    color: #0f0f0f;
    background: #ffffff;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    &:hover {
      color: linear-gradient(90deg, #00eb81 0%, #00c89d 100%);
      background: #ffffff;
      border: 1px solid #00eb81;
      color: #00eb81;
    }
  }

  .qa-answer-text {
    font-weight: 400;
    font-size: 14px;
    color: #515350;
  }
}

.qa-contact {
  margin-top: 40px;
  padding: 32px 40px;
  max-width: 760px;
  background: #f9fafb;
  border-radius: 24px;
  // box-shadow: 0 16px 40px rgba(15, 17, 21, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.qa-contact__info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qa-contact__title {
  font-size: 18px;
  font-weight: 600;
  color: #101828;
  margin: 0;
}

.qa-contact__desc {
  font-size: 14px;
  color: rgba(16, 24, 40, 0.7);
  margin: 0;
}

.qa-contact__button {
  min-width: 120px;
  padding: 12px 24px;
  background: linear-gradient(132deg, #00eb81 0%, #00c89d 100%);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 12px 20px rgba(0, 200, 157, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 30px rgba(0, 200, 157, 0.26);
  }

  &:active {
    transform: translateY(0);
  }
}

// 直播间模块
.live-room-section {
  background: #f9fafb;
}

.live-room-layout {
  display: grid;
  grid-template-columns: 360px 1fr;
  align-items: center;
  gap: 48px;
}

.live-room-info {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.live-room-tabs {
  display: flex;
  flex-direction: column;
}

.live-room-tab {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0 32px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  font-family: inherit;

  &:focus {
    outline: none;
    border-left-color: #00c89d;
  }
  &:hover {
    border-left-color: #00c89d;
  }
}

.live-room-tab__title {
  font-weight: 600;
  font-size: 20px;
  color: #0d1400;
  line-height: 28px;
}

.live-room-tab__desc {
  font-size: 14px;
  color: #515350;
  line-height: 22px;
  max-width: 280px;
}

.live-room-tab--active {
  border-left-color: #00c89d;
}

.live-room-tab--active .live-room-tab__title {
  color: #00c89d;
}

.live-room-visual {
  display: flex;
  justify-content: center;
}

.live-room-image-wrap {
  width: 100%;
  max-width: 680px;
  border-radius: 32px;
  overflow: hidden;
  // box-shadow: 0 20px 40px rgba(15, 17, 21, 0.12);
}

.live-room-image {
  width: 100%;
  display: block;
  object-fit: cover;
}

// AI 模块
.ai-efficiency-section {
  width: 100%;
  background: #05070c;
  padding: 40px 0;
  position: relative;
  height: 600px;
}

.ai-efficiency-bg {
  position: relative;
  width: 100%;
  padding: 45px 0;
  overflow: hidden;
  z-index: 3;
  height: 100%;
  display: flex;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: var(--ai-bg-image);
    background-size: cover;
    background-position: center;
    opacity: 0.15;
    pointer-events: none;
  }
}

.ai-efficiency-container {
  position: relative;
  z-index: 1;
  width: 1232px;
  max-width: 90%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 32px;
  color: #ebf0f5;
}

.ai-efficiency-tag {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 24px;
  border-radius: 999px;
  background: rgba(0, 200, 157, 0.12);
  border: 1px solid rgba(0, 200, 157, 0.4);
  color: #00eb81;
  font-size: 16px;
  font-weight: 600;
}

.ai-efficiency-title {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  color: #ffffff;
}

.ai-efficiency-subtitle {
  font-size: 18px;
  color: rgba(235, 240, 245, 0.7);
  max-width: 720px;
}

.ai-efficiency-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  width: 100%;
}

.ai-efficiency-card {
  position: relative;
  padding: 32px;
  border-radius: 24px;
  background: linear-gradient(
      125deg,
      rgba(3, 199, 136, 0.14) 0%,
      rgba(3, 199, 136, 0) 62%
    ),
    rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(0, 200, 157, 0.16);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(16px);
  display: flex;
  gap: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  flex-direction: column;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 30px 50px rgba(0, 235, 129, 0.2);
    border-color: rgba(0, 200, 157, 0.5);
  }
}

.ai-efficiency-card__icon {
  gap: 20px;
  display: flex;
  align-items: center;

  img {
    width: 58px;
    height: 61px;
    object-fit: contain;
    margin-top: 12px;
  }

  h3 {
    margin-top: 12px;
  }
}

.ai-efficiency-card__content {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ai-efficiency-card__title {
  font-size: 30px;
  font-weight: 600;
  color: #ffffff;
}

.ai-efficiency-card__desc {
  font-size: 20px;
  color: #fff;
  font-weight: 500;
}

.ai-efficiency-card__text {
  font-size: 14px;
  color: rgba(235, 240, 245, 0.72);
  line-height: 1.8;
}

// 作业模块
.homework-section {
  width: 100%;
  padding: 40px 0;
  background: #f9fafb;
}

.homework-container {
  width: 1200px;
  max-width: 95%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  text-align: center;
}

.homework-title {
  font-size: 40px;
  font-weight: 700;
  color: #101828;
}

.homework-tabs {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(16, 24, 40, 0.06);
  box-shadow: inset 0 1px 2px rgba(16, 24, 40, 0.12);
}

.homework-tab {
  position: relative;
  padding: 12px 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(16, 24, 40, 0.55);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 200, 157, 0.3);
  }
}

.homework-tab--active {
  background: #ffffff;
  color: #101828;
  font-weight: 600;
  box-shadow: 0 12px 24px rgba(15, 17, 21, 0.12);
}

.homework-panel {
  width: 100%;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 48px;
  align-items: center;
  background: #ffffff;
  border-radius: 40px;
  padding: 8px;
  height: 448px;
  box-shadow: 0 24px 60px rgba(15, 17, 21, 0.08);
}

.homework-slide-enter-active,
.homework-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.homework-slide-enter {
  opacity: 0;
  transform: translateX(30px);
}

.homework-slide-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.homework-slide-leave {
  opacity: 1;
  transform: translateX(0);
}

.homework-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.homework-info {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-left: 48px;
}

.homework-panel-title {
  font-size: 32px;
  font-weight: 700;
  color: #101828;
}

.homework-panel-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: rgba(16, 24, 40, 0.8);
}

.homework-panel-text {
  font-size: 16px;
  color: rgba(16, 24, 40, 0.7);
  line-height: 1.7;
  margin-bottom: 70px;
}

.homework-feature-list {
  margin: 0;
  padding: 12px 0 0;
  list-style: none;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.homework-feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #101828;
  font-weight: 500;
}

.homework-feature-item .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00eb81 0%, #00c89d 100%);
  box-shadow: 0 4px 10px rgba(0, 200, 157, 0.4);
}

.homework-visual {
  display: flex;
  justify-content: center;
}

.homework-visual-card {
  width: 100%;
  // padding: 32px;
  background: linear-gradient(145deg, #f5f7ff 0%, #ffffff 60%);
  border-radius: 32px;
  // box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8),
  //   0 20px 40px rgba(15, 17, 21, 0.12);
}

.homework-visual-image {
  width: 100%;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(15, 17, 21, 0.08);
}

// 真实用户故事
.stories-section {
  padding: 40px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f7f8fa 50%, #ffffff 100%);
}

.stories-container {
  width: 1200px;
  max-width: 95%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
}

.stories-title {
  font-size: 40px;
  font-weight: 700;
  color: #101828;
  text-align: center;
}

.stories-marquee {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.stories-row {
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 15%,
    rgba(255, 255, 255, 1) 85%,
    rgba(255, 255, 255, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 15%,
    rgba(255, 255, 255, 1) 85%,
    rgba(255, 255, 255, 0) 100%
  );
}

.stories-track {
  display: flex;
  gap: 24px;
  align-items: stretch;
  width: max-content;
  animation: stories-marquee-left 40s linear infinite;
}

.stories-row--bottom .stories-track {
  animation-name: stories-marquee-right;
}

.stories-row:hover .stories-track {
  animation-play-state: paused;
}

.story-card {
  width: 360px;
  flex: 0 0 360px;
  min-height: 190px;
  background: #eee;
  border-radius: 24px;
  padding: 28px;
  border: 1px solid rgba(16, 24, 40, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
}

.story-desc {
  font-size: 15px;
  color: rgba(15, 17, 21, 0.78);
  line-height: 1.8;
  margin: 0;
}

.story-user {
  display: flex;
  align-items: center;
  gap: 12px;
}

.story-avatar-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;
}

.story-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.story-user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.story-name {
  font-weight: 600;
  font-size: 16px;
  color: #101828;
  margin: 0;
}

.story-position {
  font-size: 14px;
  color: rgba(15, 17, 21, 0.58);
  margin: 0;
}

@keyframes stories-marquee-left {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes stories-marquee-right {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
}

// 常见问题
.qa-section {
  .container {
    max-width: 700px;
  }

  .qa-list {
    display: flex;
    flex-direction: column;
    position: relative;
    flex-flow: column;
    flex: none;
    place-content: center;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: min-content;
    padding: 0;
    overflow: visible;
    transition: all 0.3s;
    .qa-item {
      width: 100%;
      border-radius: 19px 19px 19px 19px;
      border: 1px solid #ebf0f5;
      .qa-question {
        flex-flow: row;
        flex: none;
        place-content: flex-start center;
        align-items: flex-start;
        gap: 12px;
        width: 100%;
        height: min-content;
        padding: 18px;
        display: flex;
        position: relative;
        overflow: visible;
        cursor: pointer;
        .qa-content {
          white-space: pre-wrap;
          word-break: break-word;
          word-wrap: break-word;
          flex: 1 0 0;
          width: 1px;
          height: auto;
          position: relative;
        }
        &:hover .qa-question-icon-wrap {
          background-color: rgb(239, 242, 245);
        }
      }
      .qa-question-icon-wrap {
        flex-flow: row;
        flex: none;
        place-content: center;
        align-items: center;
        gap: 10px;
        width: min-content;
        height: min-content;
        padding: 6px;
        display: flex;
        position: relative;
        overflow: visible;
        border-radius: 99px;
        background-color: rgb(249, 250, 251);
        .qa-question-icon {
          aspect-ratio: 1;
          height: 8px;
          flex: none;
          width: 8px;
          position: relative;
          overflow: hidden;

          > div {
            width: 10px;
            height: 2px;
            position: absolute;
            top: calc(50% - 1px);
            left: calc(50% - 5px);
            overflow: hidden;
            background-color: rgb(105, 114, 137);
            transition: transform 0.3s ease;
          }
          .qa-question-icon-cross {
            transform: rotate(270deg);
          }
        }
      }
      .qa-answer {
        cursor: default;
        flex-flow: row;
        flex: none;
        place-content: flex-start;
        align-items: flex-start;
        gap: 10px;
        width: 100%;
        max-height: 0;
        padding: 0 24px;
        display: flex;
        position: relative;
        overflow: hidden;
        margin-top: 0;
        opacity: 0;
        transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease,
          margin-top 0.3s ease;
      }
      &.action {
        .qa-item {
          border: none;
        }
        .qa-item-wrap {
          background-color: #f9fafb;
          border-radius: 19px;
        }
        .qa-answer {
          max-height: 320px;
          opacity: 1;
          padding: 12px 24px 24px;
          margin-top: 14px;
        }
        .qa-question {
          border: 1px solid #ebf0f5;
          background-color: rgb(255, 255, 255);
          border-radius: 18px;
          box-shadow: rgba(0, 0, 0, 0.06) 0px 1px 2px 0px,
            rgba(0, 0, 0, 0.01) 0px 0.361312px 0.650362px -0.25px,
            rgba(0, 0, 0, 0.02) 0px 1.37312px 2.47162px -0.5px,
            rgba(0, 0, 0, 0.05) 0px 6px 10.8px -0.75px;
        }
        .qa-question-icon-wrap .qa-question-icon .qa-question-icon-cross {
          transform: rotate(0);
        }
      }
    }
    .qa-text {
      font-weight: 500;
      font-size: 14px;
      color: #0f1115;
      line-height: 20px;
      text-align: left;
      font-style: normal;
      text-transform: none;

      ::v-deep a {
        color: #00eb81;
        margin: 0 1px;
      }
    }
  }
}

// 微信二维码区域
.wechat-entry-section {
  padding: 0;
}

.wechat-entry-bg {
  width: 100%;
  background: linear-gradient(135deg, #e8f4ff 0%, #f0f9ff 50%, #e8f5f0 100%);
  padding: 24px 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: var(--bg-img-left), var(--bg-img-right);
    background-position: left center, right center;
    background-repeat: no-repeat;
    background-size: auto 100%;
    opacity: 0.3;
    pointer-events: none;
  }
}

.wechat-entry-title {
  font-weight: 600;
  font-size: 16px;
  color: #0d1400;
  text-align: center;
  margin-bottom: 24px;
  line-height: 1.4;
  position: relative;
  z-index: 1;
}

.wechat-entry-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.qr-code-wrap {
  flex-shrink: 0;
  width: 84px;
  height: 84px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.wechat-entry-text {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.desc-line {
  font-size: 12px;
  color: #0d1400;
  line-height: 1;
  margin: 0;
}
.free-trial-btn,
.consult-btn {
  width: 80px;
  height: 26px;
  background: linear-gradient(132deg, #00eb81 0%, #00c89d 100%);
  border: none;
  border-radius: 99px;
  font-weight: 600;
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  transition: transform 0.3s ease;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px 0 rgba(0, 235, 129, 0.3);
  }
}
.ssr-variant {
  display: contents;
  .framer-1stzqw2-container {
    aspect-ratio: 1;
    height: 540px;
    z-index: 2;
    flex: none;
    width: 540px;
    position: absolute;
    top: 28px;
    left: 50%;
    transform: translate(-50%);
  }
  .framer-gzvjR {
    height: 100%;
    max-width: 100%;
    width: 100%;
    opacity: 1;
    position: relative;
    overflow: visible;
  }
  .framer-1ehqw8y {
    background: linear-gradient(
      rgba(255, 255, 255, 0.03) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    opacity: 1;
    border-radius: 500px;
    aspect-ratio: 1;
    height: 100%;
    will-change: transform;
    z-index: 0;
    flex: none;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
  }
  .framer-xv7igh {
    backdrop-filter: blur(11px);
    background: linear-gradient(
      rgba(5, 5, 5, 0.76) 0%,
      rgba(13, 13, 13, 0.79) 100%
    );
    filter: saturate(1.17);
    transform: translate(-50%, -50%);
    opacity: 1;
    border-radius: 500px;
    box-shadow: rgba(0, 0, 0, 0.173) 0px 0.764039px 0.687635px -0.5px,
      rgba(0, 0, 0, 0.17) 0px 1.87166px 1.68449px -1px,
      rgba(0, 0, 0, 0.165) 0px 3.54652px 3.19187px -1.5px,
      rgba(0, 0, 0, 0.16) 0px 6.19129px 5.57216px -2px,
      rgba(0, 0, 0, 0.15) 0px 10.7756px 9.69802px -2.5px,
      rgba(0, 0, 0, 0.125) 0px 19.7367px 17.7631px -3px,
      rgba(0, 0, 0, 0.075) 0px 39px 35.1px -3.5px;
    aspect-ratio: 1;
    height: 99%;
    will-change: transform;
    z-index: 0;
    flex: none;
    width: 99%;
    position: absolute;
    top: 50%;
    left: 50%;
    overflow: hidden;
  }
  .framer-xknqa4 {
    backdrop-filter: blur(11px);
    background: linear-gradient(
      rgb(0, 0, 0) 0%,
      rgba(255, 255, 255, 0.13) 100%
    );
    transform: translate(-50%, -50%);
    opacity: 1;
    border-radius: 500px;
    aspect-ratio: 1;
    height: 92%;
    will-change: transform;
    z-index: 0;
    flex: none;
    width: 92%;
    position: absolute;
    top: 50%;
    left: 50%;
    overflow: hidden;
  }
  .framer-1p1pzny {
    background-color: rgb(0, 1, 5);
    transform: translate(-50%, -50%);
    opacity: 1;
    border-radius: 500px;
    aspect-ratio: 1;
    height: 91%;
    will-change: transform;
    z-index: 0;
    flex: none;
    width: 91%;
    position: absolute;
    top: 50%;
    left: 50%;
    overflow: hidden;
  }
  .framer-166olkj {
    opacity: 1;
    flex: none;
    width: 91%;
    height: 92%;
    position: absolute;
    top: 4.00485%;
    left: 4.36893%;
    overflow: visible;

    .framer-1socfl3 {
      background-color: rgba(255, 255, 255, 0.01);
      opacity: 1;
      z-index: 4;
      flex: none;
      width: 100%;
      height: 0%;
      position: absolute;
      top: 49.7362%;
      left: 0;
      overflow: hidden;
    }
    > div {
      --border-bottom-width: 2px;
      --border-color: rgb(255, 255, 255);
      --border-left-width: 2px;
      --border-right-width: 2px;
      --border-style: solid;
      --border-top-width: 2px;
      opacity: 0.01;
      transform: translate(-50%, -50%);
      border-radius: 500px;
      aspect-ratio: 1;
      height: 85%;
      width: 85%;
      will-change: transform;
      z-index: 4;
      flex: none;
      position: absolute;
      top: 50%;
      left: 50%;
      overflow: hidden;
      &::after {
        content: "";
        border-width: 2px 2px 2px 2px;
        border-color: rgb(255, 255, 255);
        border-style: solid;
        box-sizing: border-box;
        border-radius: inherit;
        pointer-events: none;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
    .framer-y8aghn {
      height: 85%;
      width: 85%;
    }
    .framer-1w9dmf0 {
      height: 66%;
      width: 66%;
    }
    .framer-88dz9y {
      height: 46%;
      width: 46%;
    }
    .framer-trql9i {
      height: 24%;
      width: 24%;
    }
  }

  .framer-1bce4jm {
    background: conic-gradient(
      from 0deg at 50% 50%,
      #00eb81 0.0444deg,
      rgba(0, 0, 0, 0) 16.4002deg,
      rgba(0, 0, 0, 0) 322.7245deg,
      #00c89d 345.2824deg
    );

    transform: translateX(-50%) rotate(var(--rotate-angle));
    will-change: transform;
    opacity: 0.5625;
    border-radius: 500px;
    aspect-ratio: 1;
    height: 100%;
    will-change: transform;
    z-index: 0;
    flex: none;
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 50%;
    overflow: hidden;
  }
  .framer-19bj41o {
    // background: conic-gradient(rgb(59, 59, 59) 0deg, rgb(255, 255, 255) 360deg);
    background: conic-gradient(
      rgb(88, 203, 135) 0deg,
      rgb(236 247 239 / 39%) 360deg
    );
    will-change: transform;
    opacity: 1;
    border-radius: 500px;
    aspect-ratio: 1;
    mix-blend-mode: color-dodge;
    height: 91%;
    width: 91%;
    will-change: transform;
    z-index: 5;
    flex: none;
    position: absolute;
    top: 50%;
    left: 50%;
    overflow: hidden;
    transform: translate(-50%, -50%) rotate(var(--rotate-angle));
  }
}
.ai-efficiency-bg-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 426px;
  z-index: 2;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 71%);
  border-radius: 0px 0px 0px 0px;
}
</style>
